/**
 * LaterCon
 * --------
 */

/* Imports */

import React from "react";
import { StaticImage } from "gatsby-plugin-image";
import { graphql } from "gatsby";
import { speakers, faqs } from "../latercon/2021/base/data";
import Button from "../latercon/2021/components/Button";
import Ghost from "../latercon/2021/components/Ghost";
import Logo from "../latercon/2021/components/Logo";
import Logos from "../latercon/2021/components/Logos";
import Swap from "../latercon/2021/components/Swap";
import Marquee from "../latercon/2021/components/Marquee";
import Sticky from "../latercon/2021/components/Sticky";
import Blob from "../latercon/2021/components/Blob";
import Collapsible from "../latercon/2021/components/Collapsible";
import SeoHead from "../components/SeoHead";
import Layout from "../components/layouts/latercon/2021/Layout";
import { getPageProperties } from "../templates/models/ModelPage";

/* Variables */

const ghostPath = `../../static/latercon/2021/img/ghosts/ghost-`;
const decoPath = `../../latercon/2021/svg/deco/`;

/* Page */

const LaterCon = ({
  showLogos = true,
  showSpeakers = true,
  registrationClosed = true,
  eventPassed = true,
  data,
}) => {
  const {
    previewImage,
    schema,
    notificationBanner,
    showBanner,
    isTrialPage,
    sourceQueryParam,
    secondaryNavigation,
    breadcrumbName,
    breadcrumbTrail,
    breadcrumbSchemaOnly,
    mainNavigation,
    mainNavigationCtaText,
    mainNavigationCtaLink,
    footerTheme,
    metadata,
    slug,
    businessUnit,
    template,
    categories,
  } = getPageProperties(data?.page);

  const disableLinks = registrationClosed || eventPassed;
  const ctaText = disableLinks ? "Registration Closed" : "Get Your Free Ticket";

  return (
    <Layout
      registrationClosed={ registrationClosed }
      eventPassed={ eventPassed }
      ctaText={ ctaText }
      businessUnit={ businessUnit }
    >
      <section className="lc-pad-v-t lc-p-r lc-o-h" data-size="xxxl">
        <Ghost
          id="g1"
          img={
            <StaticImage
              src={ `${ghostPath}1.png` }
              alt=""
              quality={ 75 }
              placeholder="none"
            />
          }
        />
        <Ghost
          id="g2"
          img={
            <StaticImage
              src={ `${ghostPath}2.png` }
              alt=""
              quality={ 75 }
              placeholder="none"
            />
          }
        />
        <div className="lc-purple lc-p-r lc-ta-c">
          <div className="lc-pad-h-pc" data-size="l">
            <div className="lc-container-xs">
              <Logo />
              <div className="lc-o-h lc-pad-h-pc" data-size="s">
                <h2
                  className="lc-h3-xs lc-pad-v-b lc-m-0 lc-move-fade-y lc-vis"
                  data-size="r"
                  data-mobile
                  data-delay="700"
                >
                  <div role="text">
                    A social media conference by Later.{ " " }
                    <time className="lc-br" dateTime="2021-09-29">
                      September 29 2021
                    </time>
                  </div>
                </h2>
              </div>
            </div>
          </div>
          <div className="lc-container-xs">
            <Button
              text={ ctaText }
              link="https://hopin.com/events/latercon"
              classes="lc-scale lc-vis GTM-LATERCON-CTA-CLICK"
              data-delay="800"
            />
          </div>
        </div>
        { showLogos ? (
          <div className="lc-pad-v-t" data-size="xl" data-mobile>
            <div className="lc-container-m lc-p-r">
              <Logos />
            </div>
          </div>
        ) : (
          ""
        ) }
        <div
          className="lc-gradient lc-pad-v-b"
          data-size={ `xx${showLogos ? "x" : ""}l` }
          data-mobile
        />
      </section>

      <section>
        <div className="lc-container-s lc-ta-c">
          <h2 className="lc-purple lc-pad-h-pc lc-mb-15 lc-p-r" data-size="s">
            <div className="lc-v-h">
              Social media is always changing, adapting and moving
            </div>
            <div
              className="lc-line lc-flex lc-vis"
              aria-hidden="true"
              data-align="center"
              data-justify="center"
              data-wrap
              data-v-done="1200"
              data-v-done-pub="lc-swap"
              data-v-all
            >
              <div>
                <div className="lc-p-r">
                  <div
                    className="lc-deco lc-move-fade-y"
                    data-type="arrows"
                    data-reverse
                  >
                    <img
                      className="lc-deco__d1"
                      src={ `${decoPath}three-arrows.svg` }
                      alt=""
                    />
                  </div>
                  <div className="lc-o-h" style={ { paddingTop: "2%" } }>
                    <div className="lc-move-scale-y">Social</div>
                  </div>
                </div>
              </div>
              <div>
                <div className="lc-p-r">
                  <div className="lc-deco lc-o-h" data-type="dash2">
                    <img
                      className="lc-move-fade-x lc-100"
                      data-x="100"
                      style={ { "--delay": "600ms" } }
                      src={ `${decoPath}dash-sm.svg` }
                      alt=""
                    />
                  </div>
                  <img
                    className="lc-deco lc-scale"
                    style={ { "--delay": "300ms" } }
                    data-type="verified"
                    data-sc="l"
                    src={ `${decoPath}verified.svg` }
                    alt=""
                  />
                  <div
                    className="lc-o-h"
                    style={ { paddingTop: "2%", width: "101%" } }
                  >
                    <div
                      className="lc-move-scale-y lc-ta-l"
                      style={ { "--delay": "50ms" } }
                    >
                      media
                    </div>
                  </div>
                </div>
              </div>
              <div className="lc-o-h">
                <div
                  className="lc-move-scale-y lc-d-b"
                  style={ { "--delay": "100ms" } }
                >
                  is
                </div>
              </div>
              <div className="lc-o-h">
                <div
                  className="lc-move-scale-y lc-d-b"
                  style={ { "--delay": "150ms" } }
                >
                  always
                </div>
              </div>
              <Swap words={ [
                {
                  word: "changing",
                  base: true
                },
                {
                  word: "adapting"
                }, {
                  word: "moving"
                }
              ] } />
            </div>
          </h2>
          <div className="lc-purple lc-pad-h-pc" data-size="s">
            <h3
              className="lc-h3-xs lc-pad-v-b lc-m-0"
              data-size="xxxs"
              data-mobile
            >
              <div className="lc-p-r lc-d-ib">
                <div
                  className="lc-deco lc-flex lc-vis"
                  aria-hidden="true"
                  data-type="date2"
                  data-col
                  data-v-all
                >
                  <span className="lc-deco__tx1 lc-fade">29:09:21</span>
                  <div className="lc-deco__d1 lc-lh-0">
                    <img
                      className="lc-deco__spin"
                      src={ `${decoPath}star.svg` }
                      alt=""
                    />
                  </div>
                  <img
                    className="lc-deco__d2 lc-move-fade-y"
                    src={ `${decoPath}dash-bend-right.svg` }
                    alt=""
                  />
                </div>
                <div className="lc-o-h">
                  <div className="lc-move-fade-y lc-vis">
                    That’s where we come in.
                  </div>
                </div>
              </div>
            </h3>
          </div>
        </div>
        <div className="lc-container-xs lc-ta-c">
          <p className="lc-move-fade-y lc-vis">
            We’re bringing together 25+ industry experts including top brands
            and creators to talk about real marketing strategies and trends.
            They’ll cover everything from digital strategy to content creation,
            and so much more.
          </p>
        </div>

        <div className="lc-pad-v-t" data-size="xl" data-mobile>
          <Marquee sticky="false" />
        </div>

        <Sticky />
      </section>

      { showSpeakers ? (
        <div className="lc-pad-v-b" data-size="xl" data-mobile>
          <Marquee ariaHidden="true" sticky="false" />
          <div id="speakers"></div>
        </div>
      ) : (
        ""
      ) }

      <div className="lc-o-h">
        { showSpeakers ? (
          <section className="lc-p-r lc-z-1">
            <div className="lc-container lc-ta-c">
              <div className="lc-pad-v-b lc-brown" data-size="r" data-mobile>
                <h2 className="lc-h2-s lc-fade lc-vis">Speakers</h2>
              </div>
              <div className="lc-pad-v-b" data-size="l" data-mobile>
                <div className="lc-flex" data-gap="r-alt" data-wrap data-mobile>
                  { speakers.map((s, i) => {
                    return (
                      <Blob
                        index={ i }
                        key={ `bb-${i}` }
                        id={ `bb-${i}` }
                        type={ s.type }
                        img={ s.img }
                        title={ s.name }
                        subtitle={ s.title }
                        links={ s.links }
                        link={ s.link }
                      />
                    );
                  }) }
                </div>
              </div>
              <div className="lc-orange">
                <Button
                  outline
                  theme="orange"
                  text="See Full Lineup"
                  link="https://hopin.com/events/latercon#speakers"
                  classes="lc-scale lc-vis GTM-LATERCON-CTA-CLICK"
                  canDisable={ false }
                />
              </div>
            </div>
          </section>
        ) : (
          <>
            <Marquee ariaHidden="true" sticky="false" />
            <div className="lc-pad-v-b" data-size="xxl" data-mobile />
            <div className="lc-container lc-ta-c lc-p-r lc-z-1">
              <h2 className="lc-h2-s lc-brown lc-fade lc-vis">
                Speaker lineup coming soon
              </h2>
            </div>
          </>
        ) }
        <section className="lc-pad-v-t lc-p-r" data-size="xxl" data-mobile>
          <div className="lc-pad-v-t" data-size="xxxs" data-mobile />
          <Ghost
            id="g4"
            img={
              <StaticImage
                src={ `${ghostPath}3.png` }
                alt=""
                quality={ 75 }
                placeholder="none"
              />
            }
          />
          <Ghost
            id="g3"
            img={
              <StaticImage
                src={ `${ghostPath}1.png` }
                alt=""
                quality={ 75 }
                placeholder="none"
              />
            }
          />
          <div
            className="lc-pad-v-b lc-ta-c lc-p-r"
            data-size="xxl"
            data-mobile
          >
            <div className="lc-container-s">
              <h2
                className="lc-purple lc-mb-15 lc-pad-h-pc lc-p-r"
                data-size="s"
              >
                <div className="lc-v-h">Join us at LaterCon 2021</div>
                <div
                  className="lc-line lc-flex lc-vis"
                  aria-hidden="true"
                  data-align="center"
                  data-justify="center"
                  data-wrap
                  data-v-all
                >
                  <div className="lc-p-r" style={ { paddingRight: "4%" } }>
                    <div className="lc-deco" data-type="free">
                      <div className="lc-deco__e lc-scale" data-sc="l">
                        <div>Totally Free!</div>
                      </div>
                    </div>
                    <div className="lc-o-h" style={ { width: "107%" } }>
                      <div className="lc-move-scale-y lc-ta-r">Join</div>
                    </div>
                  </div>
                  <div className="lc-o-h">
                    <div
                      className="lc-move-scale-y"
                      style={ { "--delay": "50ms" } }
                    >
                      us
                    </div>
                  </div>
                  <div className="lc-p-r">
                    <div
                      className="lc-deco lc-flex lc-move-fade-y"
                      data-type="virtual"
                      data-col
                      data-reverse
                      style={ { "--delay": "75ms" } }
                    >
                      <div className="lc-deco__tx1">virtually</div>
                      <img
                        className="lc-deco__d1"
                        src={ `${decoPath}wand.svg` }
                        alt=""
                      />
                    </div>
                    <div className="lc-o-h" style={ { width: "104%" } }>
                      <div
                        className="lc-move-scale-y lc-ta-l"
                        style={ { "--delay": "100ms" } }
                      >
                        at
                      </div>
                    </div>
                  </div>
                  <div className="lc-o-h">
                    <div
                      className="lc-move-scale-y"
                      style={ { "--delay": "150ms" } }
                    >
                      Later
                      <span className="lc-line__display lc-ff-display lc-d-ib">
                        Con
                      </span>
                    </div>
                  </div>
                  <div className="lc-p-r">
                    <div className="lc-o-h">
                      <div
                        className="lc-move-scale-y"
                        style={ { "--delay": "200ms" } }
                      >
                        2021
                      </div>
                    </div>
                    <div className="lc-deco lc-flex" data-type="date3">
                      <div
                        className="lc-deco__tx1 lc-fade"
                        style={ { "--delay": "250ms" } }
                      >
                        September
                      </div>
                      <div
                        className="lc-deco__d lc-flex lc-move-fade-x"
                        data-align="end"
                        data-col
                        data-x="-100"
                        style={ { "--delay": "250ms" } }
                      >
                        <img
                          className="lc-deco__d1"
                          src={ `${decoPath}dash-bend-sm.svg` }
                          alt=""
                        />
                        <span className="lc-deco__tx2">29</span>
                        <span className="lc-deco__tx3">September 29</span>
                        <img
                          className="lc-deco__d2"
                          src={ `${decoPath}dash-bend-sm.svg` }
                          alt=""
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </h2>
            </div>
            <div className="lc-container-xxs">
              <div className="lc-purple lc-pad-h-pc" data-size="s">
                <div className="lc-pad-v-b" data-size="s" data-mobile>
                  <h3 className="lc-h3-xs lc-m-0 lc-o-h">
                    <div className="lc-move-fade-y lc-vis">
                      The only social media and digital marketing conference you
                      need to attend.
                    </div>
                  </h3>
                </div>
                <Button
                  text={ ctaText }
                  link="https://hopin.com/events/latercon"
                  classes="lc-scale lc-vis GTM-LATERCON-CTA-CLICK"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="lc-p-r lc-z-1">
          <div className="lc-container-s">
            <div className="lc-pad-h-pc-l" data-size="r">
              <div className="lc-pad-v-b" data-size="xxs">
                <h2 className="lc-h2-s lc-brown lc-fade lc-vis">
                  You may be wondering&hellip;
                </h2>
              </div>
              <div>
                { faqs.map((f, i) => {
                  const expanded = Object.prototype.hasOwnProperty.call(
                    f,
                    "expanded"
                  )
                    ? f.expanded
                    : false;

                  return (
                    <Collapsible
                      key={ `f-${i}` }
                      id={ `f-${i}` }
                      expanded={ expanded }
                      title={ f.title }
                      content={ f.content }
                      accordionGroup="lc-faqs"
                    />
                  );
                }) }
              </div>
            </div>
          </div>
        </section>

        <div className="lc-p-r">
          <Ghost
            id="g5"
            img={
              <StaticImage
                src={ `${ghostPath}2.png` }
                alt=""
                quality={ 75 }
                placeholder="none"
              />
            }
          />
          <section
            className="lc-pad-v-t lc-ta-c lc-p-r lc-z-1"
            data-size="xxxl"
            data-mobile
          >
            <div className="lc-pad-v-b lc-ta-c" data-size="xl" data-mobile>
              <div className="lc-container-s">
                <h2 className="lc-purple lc-mb-15 lc-pad-h-pc" data-size="s">
                  <div className="lc-v-h">Sound good?</div>
                  <div
                    className="lc-line lc-flex lc-vis"
                    aria-hidden="true"
                    data-align="center"
                    data-justify="center"
                    data-wrap
                    data-v-all
                  >
                    <div className="lc-p-r">
                      <div className="lc-deco" data-type="math">
                        <img
                          className="lc-move-fade-y"
                          src={ `${decoPath}math.svg` }
                          alt=""
                          data-y="-100"
                          style={ { "--delay": "50ms" } }
                        />
                      </div>
                      <div
                        className="lc-o-h"
                        style={ { width: "101%", paddingTop: "2%" } }
                      >
                        <div className="lc-move-scale-y lc-ta-l">Sound</div>
                      </div>
                    </div>
                    <div className="lc-line__display lc-ff-display lc-p-r lc-b-ib">
                      <div className="lc-o-h">
                        <div
                          className="lc-move-scale-y"
                          style={ { "--delay": "50ms" } }
                        >
                          good?
                        </div>
                      </div>
                      <div
                        className="lc-deco lc-flex lc-move-fade-y"
                        data-type="yes"
                        data-col
                        data-reverse
                        style={ { "--delay": "500ms" } }
                      >
                        <span className="lc-deco__tx1">100% yes!</span>
                        <img
                          className="lc-deco__d1"
                          src={ `${decoPath}wand.svg` }
                          alt=""
                        />
                      </div>
                      <div
                        className="lc-deco lc-flex"
                        data-type="price3"
                        data-align="end"
                      >
                        <div>
                          <div
                            className="lc-deco__e lc-scale"
                            data-sc="l"
                            data-type="price4"
                            style={ { "--delay": "600ms" } }
                          >
                            <div>$0.00</div>
                          </div>
                        </div>
                        <img
                          className="lc-deco__d1 lc-move-fade-x"
                          src={ `${decoPath}dash-bend-left.svg` }
                          alt=""
                          data-reverse
                          style={ { "--delay": "500ms" } }
                        />
                      </div>
                    </div>
                  </div>
                </h2>
              </div>
              <div className="lc-container-xxs">
                <div className="lc-purple lc-pad-h-pc" data-size="s">
                  <div className="lc-pad-v-b" data-size="s" data-mobile>
                    <h3 className="lc-h3-xs lc-m-0 lc-o-h">
                      <div className="lc-move-fade-y lc-vis">
                        Let's get you a ticket.
                      </div>
                    </h3>
                  </div>
                  <Button
                    text={ ctaText }
                    link="https://hopin.com/events/latercon"
                    classes="lc-scale lc-vis GTM-LATERCON-CTA-CLICK"
                  />
                </div>
              </div>
            </div>
          </section>
          <div className="lc-footer-buffer" />
        </div>
      </div>
    </Layout>
  );
};

/* Export */

export default LaterCon;

export const query = graphql`
  query {
    page: contentfulPage(codeId: { eq: "latercon-2021" }) {
      ...Page
    }
  }
`;

export const Head = ({ data, location }) => {
  const { title, description, robots, canonical, previewImageUrl, seoKeywords } =
    getPageProperties(data?.page);
  return (
    <SeoHead
      title={title}
      description={description}
      robots={robots}
      canonical={canonical}
      contentfulImage={previewImageUrl}
      keywords={seoKeywords || null}
      pathname={location?.pathname}
    />
  );
};
